/** Post-ride review: verdict, planned vs actual, and coaching notes. */
import type { Insights } from "@/api/schemas";
import { num } from "@/lib/format";
import { Badge } from "./ui";

export const VERDICT = {
  on_target: { label: "On target", tone: "good" },
  too_hard: { label: "Harder than planned", tone: "bad" },
  too_short: { label: "Shorter than planned", tone: "warn" },
  too_long: { label: "Longer than planned", tone: "info" },
  unplanned: { label: "Unplanned", tone: "" },
} as const;

function fmt(v: number | string | null, unit: string) {
  if (v == null) return "—";
  return typeof v === "number"
    ? `${num(v, unit === "h" ? 1 : 0)}${unit ? ` ${unit}` : ""}`
    : `${v}${unit ? ` ${unit}` : ""}`;
}

export function InsightsBlock({ i }: { i: Insights }) {
  const v = VERDICT[i.verdict];
  const tot = i.zones.low_h + i.zones.mid_h + i.zones.high_h;
  return (
    <div className="stack" style={{ gap: 10 }} data-testid="insights">
      <div className="row">
        <Badge tone={v.tone}>{v.label}</Badge>
        {i.compliance_pct != null && <span className="muted">Plan compliance {num(i.compliance_pct)}%</span>}
      </div>
      {i.items.length > 0 && (
        <div className="table-wrap" tabIndex={0} role="region" aria-label="Ride review details">
          <table aria-label="Planned vs actual">
            <thead>
              <tr>
                <th>Check</th>
                <th className="r">Planned</th>
                <th className="r">Actual</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {i.items.map((it) => (
                <tr key={it.label}>
                  <td>{it.label}</td>
                  <td className="r">{fmt(it.planned, typeof it.planned === "number" ? it.unit : "")}</td>
                  <td className="r">{fmt(it.actual, it.unit)}</td>
                  <td>
                    {it.ok == null ? (
                      ""
                    ) : it.ok ? (
                      <Badge tone="good">ok</Badge>
                    ) : (
                      <Badge tone="warn">off</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tot > 0 && (
        <div>
          <small className="muted">
            Intensity: easy {Math.round((i.zones.low_h / tot) * 100)}% · tempo{" "}
            {Math.round((i.zones.mid_h / tot) * 100)}% · hard {Math.round((i.zones.high_h / tot) * 100)}%
            (easy = under your easy limit, {i.lt1} bpm on that day)
          </small>
          <div className="split" aria-hidden>
            <span style={{ width: `${(i.zones.low_h / tot) * 100}%`, background: "var(--good)" }} />
            <span style={{ width: `${(i.zones.mid_h / tot) * 100}%`, background: "var(--warn)" }} />
            <span style={{ width: `${(i.zones.high_h / tot) * 100}%`, background: "var(--bad)" }} />
          </div>
        </div>
      )}
      {i.notes.length > 0 && (
        <ul className="notes">
          {i.notes.map((n, k) => (
            <li key={k}>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
